/**
 * studentShellViewModel — ce que la coquille de l'élève (son en-tête et sa feuille « moi ») lit
 * dans le store.
 *
 * Deux sources. Le compte connecté (`currentUser.object`), mis en forme par
 * `toStudentShellAccount`. Et la famille connue sur l'appareil (`authTokens.storedTokens` +
 * `family`), qui donne les profils vers lesquels basculer et la famille dont « Espace parent »
 * ouvre l'espace — le calcul de l'en-tête legacy `StudentMainMenuHeader.js`, sur le même
 * `groupAccountsByFamily` (`data/utility/familyUtils`), sans le refaire : le groupe qui contient
 * l'élève, ses seuls comptes élèves, et `-1` pour le groupe des comptes individuels.
 *
 * Sans famille connue (aucun compte gardé sur l'appareil), pas de profils et pas de famille : la
 * feuille n'a alors ni « Changer de profil » ni « Espace parent », comme le legacy, qui n'ouvrait
 * pas de feuille du tout.
 */
import { createSelector } from "reselect"

import { groupAccountsByFamily, selectSessionStoredTokens } from "../../../../../data/utility/familyUtils"
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import { toUIParentChildren } from "../../../adapters/parent/shared/parentChildAdapter"
import {
    toStudentShellAccount,
    type ApiStudentAccount,
    type StudentShellAccount,
} from "../../../adapters/student/shared/studentShellAdapter"
import { type UIParentChild } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentShellViewModel = StudentShellAccount & {
    /** Le compte connecté — le profil « actif » de la feuille. */
    userId: string | number | null
    /** Les comptes élèves de la famille sur l'appareil, l'élève compris. Vide sans famille connue. */
    profiles: UIParentChild[]
    /**
     * La famille qu'« Espace parent » ouvre (`-1` : les comptes individuels). `null` : pas d'accès —
     * aussi quand l'espace de l'élève a éteint le bloc Parents.
     */
    familyId: string | number | null
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type ApiStoredAccount = { userId?: string | number; roles?: string[]; firstname?: string; avatar?: string }
type ApiFamilyGroup = { familyId?: string | number; isIndividualGroup?: boolean; accounts?: ApiStoredAccount[] }

const selectStudent = (state: ReduxState): ApiStudentAccount | undefined => selectDisplayedStudent(state)
const selectStoredTokens = (state: ReduxState): ApiStoredAccount[] | undefined => selectSessionStoredTokens(state)
const selectFamily = (state: ReduxState): unknown => state?.data?.family

/** Le groupe de l'élève sur l'appareil, et ses seuls comptes élèves. Port de `StudentMainMenuHeader`. */
export function studentFamilyOf(
    userId: string | number | null | undefined,
    storedTokens: ApiStoredAccount[] | null | undefined,
    familyState: unknown
): { accounts: ApiStoredAccount[]; familyId: string | number | null } {
    if (userId == null || !storedTokens || storedTokens.length === 0) return { accounts: [], familyId: null }

    const { familyGroups } = groupAccountsByFamily(storedTokens, familyState, true) as {
        familyGroups: ApiFamilyGroup[]
    }
    const group = familyGroups.find((candidate) =>
        (candidate.accounts ?? []).some((account) => account.userId === userId)
    )
    if (!group) return { accounts: [], familyId: null }

    return {
        accounts: (group.accounts ?? []).filter((account) => account.roles?.includes("student") ?? false),
        familyId: group.isIndividualGroup ? -1 : (group.familyId ?? null),
    }
}

export const studentShellViewModel = createSelector(
    [selectStudent, selectStoredTokens, selectFamily],
    (account, storedTokens, familyState): StudentShellViewModel => {
        const userId = account?.id ?? null
        const family = studentFamilyOf(userId, storedTokens, familyState)

        return {
            ...toStudentShellAccount(account),
            userId,
            profiles: toUIParentChildren(family.accounts),
            familyId: isBlockOn(account, TENANT_BLOCKS.PARENTS) ? family.familyId : null,
        }
    }
)
